import { Card, Col, Row, Tag, Tooltip, Typography } from 'antd'
import { useEffect, useState } from 'react'
import type { TablePaginationConfig } from 'antd'
import PageTitle from '../../../components/PageTitle'
import { Contact } from '../../../components/contact/Contact'
import { Contact as ContactModel } from '../../../models/Contact'
import { ColumnsType } from 'antd/es/table'
import { FilterValue, SorterResult } from 'antd/es/table/interface'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  getContacts,
  selectContacts,
  setContactQuery,
} from '../../../store/ContactReducer'
import { selectSelectedProfile } from '../../../store/ProfileReducer'
import { TableParams } from '../../../models/TableParams'
import { initialQuery } from '../../../models/helper'
import SBTable from '../../../components/SBTable'
import { SBRMType } from '../../../modules/sbrm/SBRMModel'
import { instagramNumberFormatter } from '../../../helpers/formatters'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { extractSortDataFromSorterResult } from '../../../helpers/sorter'
import i18n from '../../../i18n/setupLocalization'
import LocalizationKeys from '../../../i18n/LocalizationKeys'

const { Text } = Typography

const ContactsPage = () => {
  dayjs.extend(relativeTime)
  const dispatch = useAppDispatch()

  const { query, isLoading } = useAppSelector((state) => state.contact)
  const { isOpen: SBRMIsOpen } = useAppSelector((state) => state.SBRM)
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true)
  const profile = useAppSelector(selectSelectedProfile())
  const items = useAppSelector(selectContacts())

  const setQueryAndGetContacts = (query: TableParams) => {
    dispatch(setContactQuery(query))
    dispatch(getContacts(query))
  }

  const columns: ColumnsType<ContactModel> = [
    {
      key: 'name',
      title: i18n.t(LocalizationKeys.Page.ContactsPage.Username),
      sorter: true,
      width: 400,
      defaultSortOrder: 'ascend',
      render: (contact: ContactModel) => (
        <Contact.Avatar size={40} id={contact.id} />
      ),
    },
    {
      key: 'following',
      title: i18n.t(LocalizationKeys.Page.ContactsPage.IsFollowing),
      sorter: true,
      width: 400,
      render: (contact: ContactModel) => (
        <Tag color={contact.isUserFollowBusiness ? 'green' : 'orange'}>
          {contact.isUserFollowBusiness ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      key: 'followers',
      title: i18n.t(LocalizationKeys.Page.ContactsPage.FollowersCount),
      sorter: true,
      width: 400,
      render: (contact: ContactModel) => (
        <Text strong>
          {instagramNumberFormatter(contact.instagramFollowerCount)}
        </Text>
      ),
    },
    {
      key: 'last-story-mention',
      title: i18n.t(LocalizationKeys.Page.ContactsPage.LastStoryMention),
      sorter: true,
      width: 400,
      render: (contact: ContactModel) => (
        <>
          {!contact.lastStoryMentionAt && ''}
          {contact.lastStoryMentionAt && (
            <Tooltip
              title={dayjs(contact.lastStoryMentionAt).format(
                'DD/MM/YYYY HH:mm'
              )}
            >
              <Tag>{dayjs(contact.lastStoryMentionAt).fromNow()}</Tag>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      key: 'last-interaction',
      title: i18n.t(LocalizationKeys.Page.ContactsPage.LastInteraction),
      sorter: true,
      width: 400,
      render: (contact: ContactModel) => (
        <Tooltip
          title={dayjs(contact.lastInteractionAt).format('DD/MM/YYYY HH:mm')}
        >
          <Tag>{dayjs(contact.lastInteractionAt).fromNow()}</Tag>
        </Tooltip>
      ),
    },
    {
      key: 'created-at',
      title: i18n.t(LocalizationKeys.Page.ContactsPage.FirstInteraction),
      sorter: true,
      width: 400,
      render: (contact: ContactModel) => (
        <Tooltip title={dayjs(contact.createdAt).format('DD/MM/YYYY HH:mm')}>
          <Tag>{dayjs(contact.createdAt).fromNow()}</Tag>
        </Tooltip>
      ),
    },
  ]

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ContactModel> | SorterResult<ContactModel>[]
  ) => {
    const newQuery = {
      ...query,
      pagination,
      filters: {
        ...query.filters,
        ...filters,
      },
      ...extractSortDataFromSorterResult<ContactModel>(sorter),
    }

    setQueryAndGetContacts(newQuery)
  }

  useEffect(() => {
    if (!isFirstRender && SBRMIsOpen) {
      return
    }
    setIsFirstRender(false)

    // Trigger on init and when SBRM is closed
    const baseQuery: TableParams = {
      ...initialQuery,
      filters: { profiles: [profile?.id!] },
      order: 'ascend',
      columnKey: 'name',
    }

    setQueryAndGetContacts(baseQuery)
  }, [dispatch, SBRMIsOpen])

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <PageTitle
          type="sympa"
          title={i18n.t(LocalizationKeys.Page.ContactsPage.Title)}
          badge={query.pagination?.total}
        />
        <Card styles={{ body: { padding: 0 } }} className="big-border-purple">
          <SBTable
            entity={SBRMType.contact}
            scroll={{ x: 800 }}
            columns={columns}
            rowKey={(record) => record.id}
            dataSource={items}
            pagination={query.pagination}
            loading={isLoading && !items.length}
            onChange={handleTableChange}
            hideCreateButton
          />
        </Card>
      </Col>
    </Row>
  )
}

export default ContactsPage
